import { create } from "zustand";
import { Asset } from "../types/asset";
import { getStackableAssets } from "../services/tradingAssetsService";

interface AssetsState {
  assets: Asset[];
  loading: boolean;
  error: string | null;
  fetchAssets: () => Promise<void>;
  resetAssets: () => void;
  getAssetByValue: (value: string) => Asset | undefined;
  isValidAsset: (value: string) => boolean;
  getFilteredAssets: (searchTerm: string) => Asset[];
}

export const useAssetsStore = create<AssetsState>((set, get) => ({
  assets: [],
  loading: false,
  error: null,

  fetchAssets: async () => {
    const { loading, assets } = get();
    if (loading || assets.length > 0) {
      return;
    }

    set({ loading: true, error: null });

    try {
      const response = await getStackableAssets();

      if (response.success && response.data) {
        set({
          assets: response.data,
          loading: false,
          error: null,
        });
      } else {
        throw new Error("Failed to fetch stackable assets");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch assets";
      set({
        error: errorMessage,
        loading: false,
        assets: [],
      });
      console.error("Error fetching assets:", err);
    }
  },

  resetAssets: () =>
    set({
      assets: [],
      loading: false,
      error: null,
    }),

  getAssetByValue: (value: string) => {
    const { assets } = get();
    return assets.find(
      (asset) => asset.asset.toLowerCase() === value.toLowerCase()
    );
  },

  isValidAsset: (value: string) => {
    const { assets } = get();
    return assets.some(
      (asset) => asset.asset.toLowerCase() === value.toLowerCase()
    );
  },

  getFilteredAssets: (searchTerm: string) => {
    const { assets } = get();
    if (!searchTerm) return assets;

    const term = searchTerm.toLowerCase();
    return assets.filter(
      (asset) =>
        asset.assetName.toLowerCase().includes(term) ||
        asset.asset.toLowerCase().includes(term)
    );
  },
}));
